// Running a closing by hand from the Centro de Control — B-40's frontend half.
//
// Pure, and out of the two dialogs for the reason `cierreCorrections.js` gives about itself: what
// breaks silently is worth testing. Three things here do.
//
//   1. **Nothing names a client.** The slots, the fields, their types and where each figure is
//      printed all come from `getCierreWorksheet`, which reads the office's profile and the
//      client's declarations. A second client is config on the backend and nothing here; a
//      `clientCode === "..."` anywhere in this file would be the start of a second list.
//   2. **An alert resolved is not a closing that agrees.** A rule whose condition still holds
//      raises a fresh alert on the next evaluation. The path to a clean closing is correcting the
//      figure, and the evaluation summary says so rather than leaving the operator to find out.
//   3. **The value is sent as typed.** `450.000` goes to the backend as `450.000`; the backend
//      parses it with the field's declared locale. Formatting or parsing a number here would be a
//      second number formatter, which is how a correction and the figure it corrected disagree.

// Today as a business date, in the browser's own calendar — a default for the picker, never a rule.
import { nombreDeAlerta, nombreDeCalculo, nombreDeDato } from "./cierreNombres";
import { nombreLegibleDeClave } from "./cierrePanel";

export const hoyISO = (ahora = new Date()) => {
  const y = ahora.getFullYear();
  const m = String(ahora.getMonth() + 1).padStart(2, "0");
  const d = String(ahora.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// What a file input should offer for a slot, from the DECLARED format. A hint for the file dialog
// only: the backend checks the bytes, and a wrong file is its 400, shown verbatim.
const ACCEPT_POR_FORMATO = {
  pdf: ".pdf,application/pdf",
  png: ".png,image/png",
  jpg: ".jpg,.jpeg,image/jpeg",
  jpeg: ".jpg,.jpeg,image/jpeg",
  xlsx: ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // B-46 — the Excel 97-2003 workbook Davivienda's offices send (recaudos, DOE).
  xls: ".xls,application/vnd.ms-excel",
  csv: ".csv,text/csv",
};

export const acceptDeFormato = (format) => ACCEPT_POR_FORMATO[String(format || "").toLowerCase()] || "";

// A declaration key as a heading: `cuadre_oficina` -> `Cuadre oficina`. Display only — the key
// itself is what is sent back, and a flat field name is never split apart to build one.
export const etiquetaDeClave = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

// A slot's latest read attempt, in the operator's words. `null` is a document nobody has read yet
// on this closing, which is not the same as one whose read failed.
// A `cardinality: many` document (one row per day) is OK when it stored rows, and EMPTY when it
// found none — and a model reads only the rows of the closing's own month, so EMPTY there most
// often means the file is from another month than the closing.
export const estadoDeLectura = (lastRead, cardinality) => {
  if (!lastRead) return { etiqueta: "Sin leer", tono: "neutral" };
  switch (lastRead.status) {
    case "OK":
      return { etiqueta: cardinality === "many" ? "Leído · tabla por día" : "Leído", tono: "ok" };
    case "EMPTY":
      return cardinality === "many"
        ? { etiqueta: "Sin renglones del mes", tono: "aviso" }
        : { etiqueta: "Leído, sin datos", tono: "aviso" };
    case "TIMEOUT":
      return { etiqueta: "Lectura agotó el tiempo", tono: "error" };
    case "ERROR":
      return { etiqueta: "Lectura rechazada", tono: "error" };
    default:
      return { etiqueta: String(lastRead.status || "—"), tono: "neutral" };
  }
};

const ORIGEN = {
  HUMAN: "digitado",
  EXTRACTION: "leído del documento",
  API: "integración",
  DERIVED: "calculado",
};

// A live datapoint as the form shows it. The value is the STORED string (`450000.00`), shown as
// stored — not reformatted into a locale here, for the third reason in the header.
export const valorVivo = (live) => {
  if (!live || live.value === null || live.value === undefined) return null;
  const origen = ORIGEN[live.origin] || (live.origin ? String(live.origin).toLowerCase() : "");
  return { valor: String(live.value), origen, por: live.recordedBy || null };
};

// The worksheet's fields grouped by the source or document that gathers them, in the order the
// backend listed them — the profile's order, which is the order the operator reads the documents.
export const agruparCampos = (fields = []) => {
  const grupos = [];
  const indice = new Map();
  fields.forEach((field) => {
    const clave = `${field.kind}:${field.sourceKey}`;
    if (!indice.has(clave)) {
      indice.set(clave, grupos.length);
      grupos.push({ clave, kind: field.kind, sourceKey: field.sourceKey, campos: [] });
    }
    grupos[indice.get(clave)].campos.push(field);
  });
  return grupos;
};

// The fields the operator actually changed — non-blank and different from what is stored. What is
// typed is compared as typed; nothing is normalised on the way.
export const cambiosPendientes = (borradores = {}, fields = []) =>
  fields
    .map((field) => ({ field, valor: String(borradores[field.fieldName] ?? "").trim() }))
    .filter(({ field, valor }) => valor !== "" && valor !== String(field.live?.value ?? ""));

// What one read did, in a sentence per fact. Keys only, as the backend answers — never a value.
// B-47 — `nombres` (the client's dictionary) and the slot's `documentKey` turn the field keys the
// read reports into the names the worksheet shows. Without them a key is prettified, never raw.
export const resumenDeLectura = (data, cardinality, { nombres, documentKey } = {}) => {
  if (!data) return [];
  // Joined as they are: a readable name may itself carry a dot ("T.INT / M.EX (ítem 19)").
  // The read reports some keys whole (`arqueo.efectivo`) and some as the bare field (`efectivo`).
  const campoDe = (clave) => String(clave).split(".").pop();
  const refDe = (clave) => {
    if (String(clave).includes(".")) return clave;
    return documentKey ? `${documentKey}.${clave}` : null;
  };
  const nombresDe = (claves) => [...new Set(claves.map((clave) => {
    const ref = refDe(clave);
    return ref ? nombreDeDato(nombres, ref) : nombreLegibleDeClave(clave);
  }))].join(", ");
  const obligatorios = data.missingRequired || [];
  // A required field that is absent was listed twice ("obligatorios que no se encontraron" and
  // "no están en el documento"); it is said once, as the required one.
  const obligatoriosPorCampo = new Set(obligatorios.map(campoDe));
  const ausentes = (data.absent || []).filter((clave) => !obligatoriosPorCampo.has(campoDe(clave)));
  const lineas = [];
  if (data.rejected) {
    lineas.push("El documento no pasó sus propias verificaciones y no se guardó ningún valor.");
  } else if (cardinality === "many" && data.status === "EMPTY") {
    lineas.push("No se encontraron renglones del mes del cierre. Revisa que el archivo sea del mismo mes que la fecha del cierre.");
  } else if (cardinality === "many" && !(data.fieldsFound || []).length) {
    lineas.push("Renglones guardados. Se usa el renglón de la fecha del cierre al presionar Evaluar.");
  } else {
    lineas.push(`${(data.fieldsFound || []).length} campos leídos.`);
  }
  if ((data.failedChecks || []).length) lineas.push(`Verificaciones fallidas: ${data.failedChecks.join("; ")}.`);
  if (obligatorios.length) lineas.push(`Obligatorios que no aparecen en el documento: ${nombresDe(obligatorios)}.`);
  if (ausentes.length) lineas.push(`No aparecen en este documento (es normal si la oficina no los tiene): ${nombresDe(ausentes)}.`);
  if ((data.unparseable || []).length) lineas.push(`Se encontraron pero no se pudieron leer: ${nombresDe(data.unparseable)}.`);
  if ((data.held || []).length) {
    lineas.push(`Se conservó el valor digitado por una persona en: ${nombresDe(data.held)}.`);
  }
  return lineas;
};

// Always said after an evaluation that left the closing needing review, because it is the one
// misreading the Alertas pane invites.
export const AVISO_ALERTAS =
  "Resolver una alerta no hace cuadrar el cierre: si la condición sigue siendo cierta, la " +
  "siguiente evaluación la vuelve a levantar. Para que cuadre, corrige el dato que la causa.";

// What an evaluation landed, in a sentence per fact.
export const resumenDeEvaluacion = (data, { nombres } = {}) => {
  if (!data) return [];
  const lineas = [];
  const estado = data.status || data.state;
  lineas.push(data.changed ? `El cierre pasó a «${estado}».` : `El cierre sigue en «${estado}».`);
  if ((data.unresolved || []).length) {
    lineas.push(`No se pudo calcular por falta de datos: ${data.unresolved.map((k) => nombreDeCalculo(nombres, k)).join(", ")}.`);
  }
  if ((data.actionableAlerts || []).length) {
    lineas.push(`Revisiones pendientes: ${data.actionableAlerts.map((k) => nombreDeAlerta(nombres, k)).join(", ")}.`);
    lineas.push(AVISO_ALERTAS);
  }
  return lineas;
};

// The picker's office options, each carrying the calendar's warning for the chosen date. A hint,
// never a gate: the backend opens the closing either way and records the exception.
export const opcionDeOficina = (office) => ({
  valor: office.officeCode,
  etiqueta: office.name ? `${office.officeCode} · ${office.name}` : office.officeCode,
  // BR-38 — a permission granted by region reaches this office only through its own region.
  region: office.region || null,
  aviso: office.businessDay && office.businessDay.closes === false
    ? `Esta oficina no cierra ese día (${office.businessDay.reason}).`
    : null,
});
